/**
 * Dynamic SEO & Structured Data (JSON-LD) Utility for RADIECE
 */

export function injectStructuredData(schema: object | object[]): void {
  if (typeof document === "undefined") return;

  const existingScript = document.getElementById("radiece-jsonld");
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement("script");
  script.id = "radiece-jsonld";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(Array.isArray(schema) ? schema : [schema]);
  document.head.appendChild(script);
}

export function getOrganizationSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "RADIECE PRIVATE LIMITED",
    legalName: "RADIECE PRIVATE LIMITED",
    alternateName: "RADIECE",
    url: "https://www.radiece.com",
    logo: "https://www.radiece.com/branding/logos/radiece_logo_primary.png",
    description: "Research And Development Infrastructure for Electronic and Computing Equipment. Enterprise software platforms, embedded hardware R&D, robotics, and cybersecurity engineering.",
    taxID: "32AAQCR1162B1ZR",
    identifier: {
      "@type": "PropertyValue",
      name: "Corporate Identification Number (CIN)",
      value: "U58200KL2026PTC104862",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Building No. 24/1701, Suite No. 1034, Heiley Offices, 2nd Floor KC Arcade, Near TV Centre, CSEZ P.O",
      addressLocality: "Kakkanad, Ernakulam",
      addressRegion: "Kerala",
      postalCode: "682037",
      addressCountry: "IN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+91-95675-51437",
      contactType: "customer service",
      email: "connect@radiece.com",
      areaServed: ["IN", "Worldwide"],
      availableLanguage: ["en", "ml"],
    },
    sameAs: [
      "https://github.com/radiece",
      "https://linkedin.com/company/radiece",
      "https://twitter.com/radiece",
    ],
  };
}

export function getBlogPostSchema(post: {
  title: string;
  excerpt: string;
  slug: string;
  publishedAt: string;
  author: { name: string; role: string };
  tags: string[];
}): object {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    url: `https://www.radiece.com/#/blog/${post.slug}`,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    keywords: post.tags.join(", "),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.radiece.com/#/blog/${post.slug}`,
    },
    author: {
      "@type": "Person",
      name: post.author.name,
      jobTitle: post.author.role,
    },
    publisher: {
      "@type": "Organization",
      name: "RADIECE PRIVATE LIMITED",
      logo: {
        "@type": "ImageObject",
        url: "https://www.radiece.com/branding/logos/radiece_logo_primary.png",
      },
    },
  };
}

export function getNewsArticleSchema(news: {
  title: string;
  summary: string;
  slug: string;
  publishedAt: string;
  author: string;
}): object {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: news.title,
    description: news.summary,
    url: `https://www.radiece.com/#/news/${news.slug}`,
    datePublished: news.publishedAt,
    dateModified: news.publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.radiece.com/#/news/${news.slug}`,
    },
    author: {
      "@type": "Organization",
      name: news.author,
    },
    publisher: {
      "@type": "Organization",
      name: "RADIECE PRIVATE LIMITED",
      logo: {
        "@type": "ImageObject",
        url: "https://www.radiece.com/branding/logos/radiece_logo_primary.png",
      },
    },
  };
}

export function getFaqSchema(faqs: Array<{ question: string; answer: string }>): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}
